import { isAlpha, isDev, isLocal } from "configuration/endpoints";
import get from "lodash/get";
import { useFeatureTogglesContext } from "./FeatureTogglesContext";

export const ALL_FEATURES: Record<
  string,
  {
    id: string;
    label: string;
    isDevOnly?: boolean;
    reloadOnToggle?: boolean; //To avoid unpredictable mix between old and new feature, for example if toggle during lineitem edit
    showInAlpha?: boolean;
  }
> = {
  campaignRedesign: {
    id: "campaignRedesign",
    label: "Campaign Redesign",
    reloadOnToggle: true,
    showInAlpha: true,
  },
  lineItemRedesign: {
    id: "lineItemRedesign",
    label: "Line item redesign",
    reloadOnToggle: true,
    showInAlpha: true,
  },
  localUserBudgetActions: {
    id: "localUserBudgetActions",
    label: "Local User Budget Actions",
    isDevOnly: true,
  },
  coldStartModel: {
    id: "coldStartModel",
    label: "Cold start model",
    reloadOnToggle: true,
    showInAlpha: true,
  },
  analyticsAIAgent: {
    id: "analyticsAIAgent",
    label: "Analytics AI Agent",
    reloadOnToggle: true,
    showInAlpha: true,
  },
  creativeSectionRedesign: {
    id: "creativeSectionRedesign",
    label: "Creative section redesign",
    showInAlpha: true,
    reloadOnToggle: true,
  },
  creativeFeatureTargeting: {
    id: "creativeFeatureTargeting",
    label: "Creative feature targeting",
    showInAlpha: true,
    reloadOnToggle: true,
  },
  bidPriceOptimization: {
    id: "bidPriceOptimization",
    label: "Bid price optimization",
    reloadOnToggle: true,
  },
};

export type Feature = keyof typeof ALL_FEATURES;

type FeaturesEnabled = {
  [key in Feature]?: boolean;
};

export function getEnabledFeaturesList(): FeaturesEnabled {
  const params = new URLSearchParams(get(window, "location.search", []));
  return Object.keys(ALL_FEATURES).reduce((acc, param) => {
    acc[param] = !!params.get(param);
    return acc;
  }, {});
}

export const useReleaseCheck = () => {
  return true;
  //after-release temporary check allowing quick rollback
};

const useToggleCheck = (featureName: Feature) => {
  const { toggles } = useFeatureTogglesContext();
  const isEnabled = !!toggles[featureName];
  const params = new URLSearchParams(get(window, "location.search", []));
  const isUrlEnabled = !!params.get(featureName);
  return isEnabled || isUrlEnabled;
};

export const useFeatureCheck = (featureName: Feature) => {
  const featureEnabled = useToggleCheck(featureName);
  const feature = ALL_FEATURES[featureName];
  return (isAlpha && !!feature?.showInAlpha) || featureEnabled;
};

export const useNoHookOnlyUrlFeatureCheck = (featureName: Feature) => {
  const params = new URLSearchParams(get(window, "location.search", []));
  return !!params.get(featureName);
};

type useFeatureTogglesListT = () => Array<{
  id: Feature;
  label: string;
  value: boolean;
  onChange: (enabled: boolean) => void;
  reloadOnToggle?: boolean;
  showInAlpha?: boolean;
}>;

export const useFeatureTogglesList: useFeatureTogglesListT = () => {
  const { toggles, setToggle } = useFeatureTogglesContext();

  return Object.entries(ALL_FEATURES)
    .filter(([, { isDevOnly }]) => {
      return isDevOnly ? isDev || isLocal : true;
    })
    .map(([featureId, { label, reloadOnToggle, showInAlpha }]) => {
      const id = featureId as Feature;

      return {
        id,
        label,
        value: !!toggles[featureId],
        onChange: (enabled: boolean) => setToggle(featureId, enabled),
        reloadOnToggle,
        showInAlpha,
      };
    });
};
